import { createSelector } from 'reselect';
import { orderBy } from 'lodash-es';

import { IndexableObjectType } from 'shared/types/common';
import { Workflow } from 'shared/types/workflow';

import { RootState } from '../..';

import { getEnclaveMap } from '../enclave/getters';

import { mergeSourcesWithWorkflow } from './utils';
import { WorkflowState } from './types';

export const getWorkflows = (state: RootState) => state.workflows;

export const getWorkflowList = createSelector<
  RootState,
  WorkflowState,
  IndexableObjectType,
  Workflow[]
>(
  getWorkflows,
  getEnclaveMap,
  (workflows, enclaveMap) => workflows.list.map((workflow) => mergeSourcesWithWorkflow(workflow, enclaveMap)),
);

export const getOrderedWorkflowList = createSelector<
  RootState,
  Workflow[],
  Workflow[]
>(
  getWorkflowList,
  (workflows) => orderBy(workflows, ['updatedAt', 'createdAt', 'name'], 'desc'),
);

export const getCreatedWorkflow = createSelector(
  getWorkflowList,
  (workflowList) => workflowList.find((workflow) => workflow.isNew),
);

export const getIsCreatingWorkflowFlag = createSelector(
  getWorkflows,
  (workflows) => workflows.isCreatingWorkflow,
);

export const getCurrentWorkflow = createSelector(
  getWorkflows,
  getEnclaveMap,
  (workflows, enclaveMap) => workflows.currentWorkflow
    ? mergeSourcesWithWorkflow(workflows.currentWorkflow, enclaveMap)
    : null,
);

export const getWorkflowById = createSelector<
  RootState,
  string,
  Workflow[],
  string,
  Workflow
>(
  getWorkflowList,
  (_, workflowGuid) => workflowGuid,
  (workflowList, workflowGuid) => workflowList.find((workflow) => (
    workflow.guid === workflowGuid
  ))!,
);

export const getFetchingCredentialsMap = createSelector(
  getWorkflows,
  (workflows) => workflows.fetchingCredentialsMap,
);

export const getIsFetchingApiCredentialsFlag = createSelector<
  RootState,
  string,
  IndexableObjectType,
  string,
  boolean
>(
  getFetchingCredentialsMap,
  (_, workflowGuid) => workflowGuid,
  (fetchingCredentialsMap, workflowGuid) => !!fetchingCredentialsMap[workflowGuid],
);

export const getIsDeletingWorkflowFlag = createSelector(
  getWorkflows,
  (workflows) => workflows.isDeletingWorkflow,
);
